import { useState, useMemo } from 'react';
import { VerificationField } from '../store/analysisSlice';
import TrustFilieBadge from './shared/TrustFilieBadge';
import { AlertTriangle } from 'lucide-react';

interface VerificationCorrection {
  doc_index: number;
  field_name: string;
  extracted_value: number;
  corrected_value: number | null;
}

interface VerificationPanelProps {
  extractedFields: VerificationField[];
  onAcceptAll: () => void;
  onSubmitCorrections: (corrections: VerificationCorrection[]) => void;
  onSkip: () => void;
  loading?: boolean;
}

export default function VerificationPanel({
  extractedFields,
  onAcceptAll,
  onSubmitCorrections,
  onSkip,
  loading = false,
}: VerificationPanelProps) {
  const [corrections, setCorrections] = useState<Record<string, number>>({});
  const lowConfidenceThreshold = 0.85;

  // Group fields by document type + index
  const grouped = useMemo(() => {
    const groups: Record<string, VerificationField[]> = {};
    for (const field of extractedFields) {
      const key = `${field.sourceDocumentType}_${field.docIndex}`;
      if (!groups[key]) groups[key] = [];
      groups[key].push(field);
    }
    return groups;
  }, [extractedFields]);

  const correctionKey = (field: VerificationField) =>
    `${field.docIndex}_${field.fieldName}`;

  const handleChange = (field: VerificationField, value: string) => {
    const key = correctionKey(field);
    const num = parseFloat(value.replace(/[^0-9.-]/g, ''));
    if (!isNaN(num) && num !== field.extractedValue) {
      setCorrections((prev) => ({ ...prev, [key]: num }));
    } else {
      setCorrections((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    }
  };

  const handleSubmit = () => {
    const correctionList: VerificationCorrection[] = extractedFields
      .filter((f) => corrections[correctionKey(f)] !== undefined)
      .map((f) => ({
        doc_index: f.docIndex,
        field_name: f.fieldName,
        extracted_value: f.extractedValue,
        corrected_value: corrections[correctionKey(f)],
      }));
    onSubmitCorrections(correctionList);
  };

  const hasCorrections = Object.keys(corrections).length > 0;
  const lowConfidenceCount = extractedFields.filter(
    (field) => typeof field.confidence === 'number' && field.confidence < lowConfidenceThreshold
  ).length;

  const fmt = (val: number) =>
    val.toLocaleString('en-US', { style: 'currency', currency: 'USD' });

  return (
    <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-6">
      <div className="flex items-start justify-between mb-4">
        <div>
          <h3 className="text-lg font-semibold text-blue-900">
            Verify Your Extracted Data
          </h3>
          <p className="text-sm text-blue-700 mt-1">
            Review what came directly from your uploaded documents before Filie recalculates your return.
          </p>
        </div>
        <button
          onClick={onSkip}
          className="text-sm text-gray-500 hover:text-gray-700 underline"
          disabled={loading}
        >
          Skip for now
        </button>
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-4">
        <TrustFilieBadge source="document" />
        <TrustFilieBadge source="user" />
        <TrustFilieBadge source="trust-filie" />
      </div>

      {lowConfidenceCount > 0 && (
        <div className="mb-4 rounded-md border border-amber-200 bg-amber-50 p-4">
          <div className="flex items-start gap-2">
            <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-600" />
            <div>
              <p className="text-sm font-medium text-amber-900">
                {lowConfidenceCount} extracted {lowConfidenceCount === 1 ? 'value needs' : 'values need'} extra attention
              </p>
              <p className="mt-1 text-sm text-amber-800">
                These amounts came from lower-confidence document reads. Double-check them before continuing.
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="space-y-4">
        {Object.entries(grouped).map(([groupKey, fields]) => {
          const docType = fields[0]?.sourceDocumentType || 'Unknown';
          const recipientName = fields[0]?.recipientName;
          const sourceName = fields[0]?.sourceName;
          return (
            <div
              key={groupKey}
              className="bg-white rounded-md border border-blue-100 p-4"
            >
              <div className="mb-3 flex flex-wrap items-start justify-between gap-3">
                <div>
                  <div className="flex flex-wrap items-center gap-2">
                    <h4 className="text-sm font-medium text-gray-700">
                      {docType}
                    </h4>
                    <TrustFilieBadge source="document" documentType={docType} />
                  </div>
                  {(sourceName || recipientName) && (
                    <p className="mt-1 text-xs text-gray-500">
                      {[sourceName, recipientName && `Recipient: ${recipientName}`].filter(Boolean).join(' · ')}
                    </p>
                  )}
                </div>
              </div>
              <div className="space-y-2">
                {fields.map((field) => {
                  const key = correctionKey(field);
                  const isCorrected = corrections[key] !== undefined;
                  const needsReview =
                    typeof field.confidence === 'number' && field.confidence < lowConfidenceThreshold;
                  return (
                    <div
                      key={key}
                      className={`flex items-center justify-between gap-4 rounded-md px-2 py-2 ${
                        needsReview ? 'bg-amber-50/70' : ''
                      }`}
                    >
                      <div className="flex-1">
                        <label className="text-sm text-gray-600">
                          {field.displayLabel}
                        </label>
                        <div className="mt-1 flex flex-wrap items-center gap-2">
                          {needsReview && (
                            <span className="inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-medium text-amber-800">
                              Needs review
                            </span>
                          )}
                          {typeof field.confidence === 'number' && (
                            <span className="text-[11px] text-gray-500">
                              {Math.round(field.confidence * 100)}% confidence
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">
                          $
                        </span>
                        <input
                          type="text"
                          defaultValue={field.extractedValue.toLocaleString(
                            'en-US',
                            { minimumFractionDigits: 2, maximumFractionDigits: 2 }
                          )}
                          onChange={(e) => handleChange(field, e.target.value)}
                          className={`w-40 pl-7 pr-3 py-1.5 text-sm border rounded-md text-right ${
                            isCorrected
                              ? 'border-amber-400 bg-amber-50'
                              : 'border-gray-300'
                          }`}
                          disabled={loading}
                        />
                        {isCorrected && (
                          <span className="ml-2 text-xs text-amber-600">
                            was {fmt(field.extractedValue)}
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      <div className="flex items-center justify-end gap-3 mt-5">
        {hasCorrections ? (
          <button
            onClick={handleSubmit}
            disabled={loading}
            className="px-4 py-2 bg-amber-600 text-white text-sm font-medium rounded-md hover:bg-amber-700 disabled:opacity-50"
          >
            {loading ? 'Recalculating...' : `Submit ${Object.keys(corrections).length} Correction(s)`}
          </button>
        ) : (
          <button
            onClick={onAcceptAll}
            disabled={loading}
            className="px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-md hover:bg-green-700 disabled:opacity-50"
          >
            {loading ? 'Confirming...' : 'All Values Look Correct'}
          </button>
        )}
      </div>
    </div>
  );
}
